import React from 'react';
import { ChevronLeft, ChevronRight, Images, X } from 'lucide-react';
import { api, isVideoUrl, MarkAlbumImage } from '../api.ts';
import { resolveUrl } from '../api.ts';
import { RADIUS, useTheme } from '../theme';
import { Badge, Button, Card, EmptyState, Icon, IconButton, Loader, SectionHeader } from '../components/ui';
import { useOverlay } from '../components/ui/shared';
import { ReportMenu } from '../components/moderation/ReportMenu';

interface AlbumTabProps {
  albumLd: boolean;
  albumImages: MarkAlbumImage[];
  setAlbumImages: React.Dispatch<React.SetStateAction<MarkAlbumImage[]>>;
  albumViewer: number | null;
  setAlbumViewer: (v: number | null) => void;
  isAdmin?: boolean;
}

// Mörk yta bakom bilder i galleriet – samma i båda teman.
const DARK = 'rgba(14,18,11,0.94)';
const onDark: React.CSSProperties = { background: 'rgba(255,255,255,0.16)', color: '#fff' };

export function AlbumTab({
  albumLd, albumImages, setAlbumImages, albumViewer, setAlbumViewer, isAdmin,
}: AlbumTabProps) {
  const T = useTheme();
  if (albumLd) return <Loader text="Laddar album…" />;

  return (
    <div>
      <SectionHeader title="Album" kicker={`${albumImages.length} ${albumImages.length === 1 ? 'bild' : 'bilder'}`} />
      {albumImages.length === 0
        ? (
          <Card>
            <EmptyState icon={Images} title="Inga bilder ännu" sub="Bilder från gruppchatten, jakterna och åtelkamerorna samlas här automatiskt." />
          </Card>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 8 }}>
            {albumImages.map((img, idx) => (
              <button key={img.id} type="button" onClick={() => setAlbumViewer(idx)} aria-label={`Visa ${img.caption || `bild ${idx + 1}`}`}
                style={{ position: 'relative', aspectRatio: '1', borderRadius: RADIUS.md, overflow: 'hidden', cursor: 'pointer', padding: 0, background: T.surface2, border: 'none', boxShadow: img.isPublic ? `0 0 0 2px ${T.amber}` : undefined }}>
                {isVideoUrl(img.imageUrl) ? (
                  <>
                    <video src={`${resolveUrl(img.imageUrl)}#t=0.1`} muted playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', background: '#000' }} />
                    <span aria-hidden style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 40, height: 40, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><polygon points="8,5 19,12 8,19" /></svg>
                    </span>
                  </>
                ) : (
                  <img src={resolveUrl(img.imageUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                )}
                {img.isPublic && <Badge tone="accent" style={{ position: 'absolute', top: 6, right: 6, background: T.accentFill, color: T.onAccent }}>Publik</Badge>}
                {(img.sourceType === 'hunt' || img.sourceType === 'trail_cam') && (
                  <Badge style={{ position: 'absolute', bottom: 6, left: 6, background: 'rgba(20,28,16,.78)', color: '#fff' }}>{img.sourceType === 'hunt' ? 'Jakt' : 'Åtelkamera'}</Badge>
                )}
              </button>
            ))}
          </div>
        )
      }

      {albumViewer !== null && albumImages[albumViewer] && (
        <AlbumViewer images={albumImages} index={albumViewer} onIndex={setAlbumViewer} isAdmin={isAdmin}
          onBlocked={img => {
            // AS1c: dölj direkt det vi vet kommer från samma person; nästa hämtning filtreras i API:t.
            setAlbumViewer(null);
            setAlbumImages(prev => prev.filter(a => a.id !== img.id && (!img.uploadedBy || a.uploadedBy !== img.uploadedBy)));
          }}
          onTogglePublic={img => {
            const newPublic = !img.isPublic;
            api.toggleAlbumImagePublic(img.id, newPublic).then(() => { setAlbumImages(prev => prev.map(a => a.id === img.id ? { ...a, isPublic: newPublic } : a)); }).catch(() => { });
          }} />
      )}
    </div>
  );
}

/** Helskärmsgalleri: pilar och ← → bläddrar, Escape stänger. */
function AlbumViewer({ images, index, onIndex, isAdmin, onTogglePublic, onBlocked }: {
  images: MarkAlbumImage[]; index: number; onIndex: (v: number | null) => void; isAdmin?: boolean; onTogglePublic: (img: MarkAlbumImage) => void;
  onBlocked: (img: MarkAlbumImage) => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const close = () => onIndex(null);
  useOverlay(true, close, ref);
  const img = images[index];
  const prev = index > 0 ? () => onIndex(index - 1) : undefined;
  const next = index < images.length - 1 ? () => onIndex(index + 1) : undefined;
  return (
    <div ref={ref} role="dialog" aria-modal="true" aria-label="Bildgalleri" tabIndex={-1}
      onKeyDown={e => { if (e.key === 'ArrowLeft' && prev) prev(); if (e.key === 'ArrowRight' && next) next(); }}
      onClick={e => { if (e.target === e.currentTarget) close(); }}
      style={{ position: 'fixed', inset: 0, background: DARK, zIndex: 3000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', outline: 'none' }}>
      <div style={{ position: 'absolute', top: 'calc(12px + env(safe-area-inset-top, 0px))', left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.75)', fontSize: 14, fontWeight: 600 }}>
        {index + 1} / {images.length}
      </div>
      <IconButton label="Stäng" onClick={close} style={{ ...onDark, position: 'absolute', top: 'calc(8px + env(safe-area-inset-top, 0px))', right: 12 }}><Icon as={X} /></IconButton>
      {prev && <IconButton label="Föregående bild" size={48} onClick={e => { e.stopPropagation(); prev(); }} style={{ ...onDark, position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}><Icon as={ChevronLeft} size={26} /></IconButton>}
      {next && <IconButton label="Nästa bild" size={48} onClick={e => { e.stopPropagation(); next(); }} style={{ ...onDark, position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}><Icon as={ChevronRight} size={26} /></IconButton>}
      {isVideoUrl(img.imageUrl)
        ? <video key={img.id} src={resolveUrl(img.imageUrl)} controls playsInline autoPlay style={{ maxWidth: '90vw', maxHeight: '75vh', borderRadius: RADIUS.md, background: '#000' }} />
        : <img src={resolveUrl(img.imageUrl)} alt={img.caption || ''} style={{ maxWidth: '90vw', maxHeight: '75vh', objectFit: 'contain', borderRadius: RADIUS.md }} />}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent,rgba(0,0,0,0.85))', padding: '40px 20px calc(20px + env(safe-area-inset-bottom, 0px))', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>{img.caption || `Bild ${index + 1} av ${images.length}`}</div>
          <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: 13, marginTop: 4 }}>{[img.uploadedBy, new Date(img.createdAt).toLocaleDateString('sv-SE'), img.sourceType === 'trail_cam' ? 'Åtelkamera' : ''].filter(Boolean).join(' · ')}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isAdmin && (
            <Button variant={img.isPublic ? 'secondary' : 'primary'} onClick={e => { e.stopPropagation(); onTogglePublic(img); }}>
              {img.isPublic ? 'Dölj från publik' : 'Gör publik'}
            </Button>
          )}
          {img.sourceType !== 'trail_cam' && (
            <ReportMenu target={{ type: 'album', id: img.id }} noun="bilden" onDark size={44} up
              ownerName={img.uploadedBy?.includes('@') ? img.uploadedBy.split('@')[0] : img.uploadedBy}
              onBlocked={() => onBlocked(img)} />
          )}
        </div>
      </div>
    </div>
  );
}
